<template>
  <new-modal-dialog
    visible
    scrollable
    max-width="600"
    persistent
    card-class="px-2"
    :title="$t('settings.user.twofa_dialog_title')"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <div v-if="loading">
      <div class="text-center py-8">
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
    </div>
    <template v-else-if="twofaSettings">
      <p class="text-body-2 text--secondary">
        {{ $t('settings.user.twofa_dialog_intro') }}
      </p>

      <!-- Email -->
      <auth-two-f-a-method-card
        v-if="twofaSettings.allowedMethods.includes('email')"
        :method-enabled="twofaSettings.enabledMethods.includes('email')"
        icon="mdi-email-outline"
        :title="$t('settings.user.twofa_verification_email_label')"
      >
        <v-btn
          v-if="!twofaSettings.enabledMethods.includes('email')"
          block
          outlined
          color="primary"
          @click="startSetup('email')"
        >
          {{ $t('settings.user.twofa_setup_email') }}
        </v-btn>
        <div
          v-else
          class="text-body-2 font-weight-medium d-flex align-center"
        >
          <v-icon
            color="success"
            class="mr-2"
          >
            mdi-check
          </v-icon>
          {{ $t('settings.user.twofa_email_enabled') }}
        </div>
      </auth-two-f-a-method-card>

      <!-- SMS -->
      <auth-two-f-a-method-card
        v-if="twofaSettings.allowedMethods.includes('sms') && twofaSettings.enabledMethods.includes('email')"
        :method-enabled="twofaSettings.enabledMethods.includes('sms')"
        icon="mdi-message-text-outline"
        :title="$t('settings.user.twofa_verification_phone_label')"
      >
        <div
          v-if="twofaSettings.enabledMethods.includes('sms')"
          class="d-flex flex-column"
        >
          <div class="text-body-2 font-weight-medium d-flex align-center mb-4">
            <v-icon
              color="success"
              class="mr-2"
            >
              mdi-check
            </v-icon>
            {{ $t('settings.user.twofa_sms_enabled') }}
          </div>
          <v-btn
            block
            outlined
            color="error"
            @click="removeSms"
          >
            {{ $t('settings.user.twofa_remove_sms') }}
          </v-btn>
        </div>
        <template v-else>
          <v-text-field
            v-model="twofaPhone"
            :label="$t('settings.user.twofa_phone_label')"
            prepend-inner-icon="mdi-phone"
            dense
            outlined
            hide-details="auto"
            class="mb-3"
          />
          <v-btn
            block
            outlined
            color="primary"
            :disabled="!twofaPhone"
            @click="startSetup('sms')"
          >
            {{ $t('settings.user.twofa_setup_sms') }}
          </v-btn>
        </template>
      </auth-two-f-a-method-card>

      <!-- Passkey -->
      <auth-two-f-a-method-card
        v-if="twofaSettings.allowedMethods.includes('passkey') && twofaSettings.enabledMethods.includes('email')"
        :method-enabled="twofaSettings.enabledMethods.includes('passkey')"
        icon="mdi-key-variant"
        :title="$t('settings.user.twofa_passkeys_label')"
      >
        <div
          v-if="twofaSettings.enabledMethods.includes('passkey')"
          class="text-body-2 font-weight-medium d-flex align-center mb-4"
        >
          <v-icon
            color="success"
            class="mr-2"
          >
            mdi-check
          </v-icon>
          {{ $t('settings.user.twofa_passkey_enabled') }}
        </div>
        <div
          v-if="(twofaSettings.passkeys || []).length > 0"
          class="mb-3"
        >
          <v-divider />
          <div class="text-overline text--secondary pt-2">
            {{ $t('settings.user.twofa_passkeys_registered') }}
          </div>
          <v-list
            dense
            class="py-0 mx-2"
          >
            <v-list-item
              v-for="pk in twofaSettings.passkeys"
              :key="pk.passkeyId"
              class="pa-0"
            >
              <v-list-item-content class="py-2">
                <v-list-item-title class="text-body-2">
                  {{ pk.label || $t('settings.user.twofa_passkey_unnamed') }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="pk.createdAt">
                  {{ $localized.numericDateTimeText(new Date(pk.createdAt)) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="my-0">
                <v-btn
                  icon
                  small
                  color="error"
                  :disabled="passkeyBusy"
                  :aria-label="$t('settings.user.twofa_passkey_remove')"
                  @click="removePasskey(pk.passkeyId)"
                >
                  <v-icon small>
                    mdi-delete-outline
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-divider class="mt-2" />
        </div>

        <div class="text-overline text--secondary mb-2">
          {{ $t('settings.user.twofa_passkeys_add_new') }}
        </div>
        <v-text-field
          v-model="passkeyLabel"
          :label="$t('settings.user.twofa_passkey_name_optional')"
          dense
          outlined
          hide-details="auto"
          class="mb-3"
        />
        <v-btn
          block
          outlined
          color="primary"
          :loading="passkeyBusy"
          @click="registerPasskey"
        >
          {{ $t('settings.user.twofa_register_passkey') }}
        </v-btn>
      </auth-two-f-a-method-card>

      <auth-two-f-a-o-t-p-dialog
        v-if="twofaSetupSession"
        :error-messages="otpErrorMessages"
        :method="twofaSetupMethod"
        @submit="verifySetup"
        @cancel="cancelSetup"
      />
    </template>

    <template #actions>
      <v-spacer />
      <v-btn
        text
        color="primary"
        @click="closeDialog"
      >
        {{ $t('button.close') }}
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { performAction } from '@/services/vue-utils';
import httpClient from '@/api/http-client';
import type { IAuth2FASettings } from '@/model/AuthSettings';
import AuthTwoFAMethodCard from '@/components/views/settings/AuthTwoFAMethodCard.vue';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';
import AuthTwoFAOTPDialog from '@/components/views/settings/AuthTwoFAOTPDialog.vue';
import i18n from '@/plugins/i18n';

const emit = defineEmits(['close', 'updated']);

const loading = ref(false);
const twofaSettings = ref<IAuth2FASettings | null>(null);
const twofaPhone = ref('');
const twofaSetupSession = ref('');
const twofaSetupMethod = ref<'sms' | 'email' | 'passkey' | ''>('');
const passkeyBusy = ref(false);
const passkeyLabel = ref('');

const otpErrorMessages = ref<string[]>([]);

const cancelSetup = () => {
  twofaSetupSession.value = '';
  twofaSetupMethod.value = '';
  otpErrorMessages.value = [];
};

const load = async () => {
  loading.value = true;
  try {
    const settings = await httpClient.authGetSettings();
    twofaSettings.value = settings;
    twofaPhone.value = settings.phone || '';
  } catch (e) {
    console.error('AuthTwoFASetupDialog load:', e);
    twofaSettings.value = null;
  } finally {
    loading.value = false;
  }
};

const closeDialog = () => {
  emit('close');
};

const startSetup = async (method: 'sms' | 'email') => {
  await performAction(
    null,
    null,
    async () => {
      const result = await httpClient.authSetupMethod(method, twofaPhone.value);
      twofaSetupSession.value = result.session;
      twofaSetupMethod.value = method;
    },
  );
};

const verifySetup = async (twofaCode: string) => {
  if (!twofaCode || twofaCode.length !== 4) return;
  const ok = await performAction(
    null,
    null,
    async () => {
      const settings = await httpClient.authVerifySetup(
        twofaSetupSession.value,
        twofaCode,
        twofaSetupMethod.value,
      );
      twofaSettings.value = settings;
      emit('updated', settings);
      cancelSetup();
      twofaPhone.value = settings.phone || '';
      emit('close');
    },
  );
  if (!ok) {
    otpErrorMessages.value = [i18n.tc('settings.user.twofa_verify_error')];
  }
};

const removeSms = async () => {
  await performAction(
    null,
    null,
    async () => {
      const settings = await httpClient.authRemoveMethod('sms');
      twofaSettings.value = settings;
      emit('updated', settings);
      twofaPhone.value = '';
    },
  );
};

const registerPasskey = async () => {
  passkeyBusy.value = true;
  try {
    await performAction(
      null,
      i18n.tc('settings.user.twofa_passkey_register_error'),
      async () => {
        const { startRegistration } = await import('@simplewebauthn/browser');
        const begin = await httpClient.authPasskeyRegisterBegin();
        if (!begin?.publicKey) {
          throw new Error(i18n.tc('settings.user.twofa_passkey_begin_invalid'));
        }
        const credential = await startRegistration({
          optionsJSON: begin.publicKey as PublicKeyCredentialCreationOptionsJSON,
        });
        const settings = await httpClient.authPasskeyRegisterFinish(
          credential as unknown as Record<string, unknown>,
          passkeyLabel.value || undefined,
        );
        twofaSettings.value = settings;
        emit('updated', settings);
        passkeyLabel.value = '';
      },
    );
  } finally {
    passkeyBusy.value = false;
  }
};

const removePasskey = async (passkeyId: number) => {
  passkeyBusy.value = true;
  try {
    await performAction(
      null,
      i18n.tc('settings.user.twofa_passkey_remove_error'),
      async () => {
        const settings = await httpClient.authPasskeyDelete(passkeyId);
        twofaSettings.value = settings;
        emit('updated', settings);
      },
    );
  } finally {
    passkeyBusy.value = false;
  }
};

onMounted(() => {
  load();
});

</script>

<style scoped lang="scss">

</style>
